/**
 * ClubSettingsPrivacyPage - Privacy & Access Settings
 * Issue #901: Split from monolithic ClubSettingsPage
 *
 * Sections:
 * - Content Moderation (clean language filter)
 * - Invite Permissions (all members / leaders only / disabled)
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useClub, useUpdateClub } from '../../hooks/useClubs';
import { TopBar, Skeleton } from '../../components';
import { Shield, MessageSquareWarning, Eye, EyeOff, Lock } from 'lucide-react';

export default function ClubSettingsPrivacyPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Data fetching
  const { data: clubData, isLoading, error } = useClub(slug || '');
  const club = clubData?.club;
  const membership = clubData?.membership;
  const isOwner = membership?.is_owner || false;

  // Mutation
  const updateClubMutation = useUpdateClub(slug || '');

  // Success message
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Clear success message after delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Handlers
  const handleToggleCleanLanguage = async () => {
    if (!club) return;
    const currentValue = club.clean_language_enabled !== 0;

    try {
      await updateClubMutation.mutateAsync({ clean_language_enabled: !currentValue });
      setSuccessMessage(`Clean language filter ${!currentValue ? 'enabled' : 'disabled'}`);
    } catch {
      // Error displayed via updateClubMutation.error state
    }
  };

  const handleInvitePermissionChange = async (permission: 'all_members' | 'leaders_only' | 'disabled') => {
    if (!club) return;

    try {
      await updateClubMutation.mutateAsync({ invite_permission: permission });
      setSuccessMessage('Invite permission updated');
    } catch {
      // Error displayed via updateClubMutation.error state
    }
  };

  const handleVisibilityChange = async (visibility: 'public' | 'private' | 'members_only') => {
    if (!club) return;

    try {
      await updateClubMutation.mutateAsync({ visibility });
      setSuccessMessage('Club visibility updated');
    } catch {
      // Error displayed via updateClubMutation.error state
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Privacy & Access" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="max-w-2xl mx-auto p-4 space-y-6">
            <Skeleton className="h-32 w-full rounded-xl" />
            <Skeleton className="h-48 w-full rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  // Error/permission state
  if (error || !isOwner || !club) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Privacy & Access" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-12 text-center">
            <p className="text-text-muted">
              {error ? 'Unable to load settings' : 'Only club owners can access settings'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar title="Privacy & Access" onBack={() => navigate(-1)} />

      <div className="flex-1 overflow-y-auto pt-14">
        <div className="max-w-2xl mx-auto p-4 space-y-6">
          {/* Success Message */}
          {successMessage && (
            <div className="rounded-lg border border-oro-success/40 bg-oro-success/10 p-4">
              <p className="text-sm text-oro-success">{successMessage}</p>
            </div>
          )}

          {/* Error Message */}
          {updateClubMutation.error && (
            <div className="rounded-lg border border-oro-error/40 bg-oro-error/10 p-4">
              <p className="text-sm text-oro-error">
                {(updateClubMutation.error as Error).message || 'An error occurred'}
              </p>
            </div>
          )}

          {/* Club Visibility Section */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
              <Eye className="w-5 h-5 text-accent" />
              Club Visibility
            </h2>

            <div className="bg-oro-surface border border-oro-border rounded-2xl p-4 space-y-3">
              <p className="text-sm text-text-secondary mb-2">
                Control who can discover and join your club.
              </p>

              {/* Public Option */}
              <label
                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                  club.visibility === 'public'
                    ? 'border-accent bg-accent/5'
                    : 'border-oro-border hover:border-oro-border-hover'
                }`}
              >
                <input
                  type="radio"
                  name="visibility"
                  value="public"
                  checked={club.visibility === 'public'}
                  onChange={() => handleVisibilityChange('public')}
                  disabled={updateClubMutation.isPending}
                  className="mt-1 accent-accent"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-text-secondary" />
                    <span className="font-medium text-text-primary">Public</span>
                  </div>
                  <p className="text-sm text-text-secondary mt-0.5">
                    Anyone can find your club and join immediately.
                  </p>
                </div>
              </label>

              {/* Members Only Option */}
              <label
                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                  club.visibility === 'members_only'
                    ? 'border-accent bg-accent/5'
                    : 'border-oro-border hover:border-oro-border-hover'
                }`}
              >
                <input
                  type="radio"
                  name="visibility"
                  value="members_only"
                  checked={club.visibility === 'members_only'}
                  onChange={() => handleVisibilityChange('members_only')}
                  disabled={updateClubMutation.isPending}
                  className="mt-1 accent-accent"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-text-secondary" />
                    <span className="font-medium text-text-primary">Members Only</span>
                  </div>
                  <p className="text-sm text-text-secondary mt-0.5">
                    Anyone can find your club, but must request approval to join.
                  </p>
                </div>
              </label>

              {/* Private Option */}
              <label
                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                  club.visibility === 'private'
                    ? 'border-accent bg-accent/5'
                    : 'border-oro-border hover:border-oro-border-hover'
                }`}
              >
                <input
                  type="radio"
                  name="visibility"
                  value="private"
                  checked={club.visibility === 'private'}
                  onChange={() => handleVisibilityChange('private')}
                  disabled={updateClubMutation.isPending}
                  className="mt-1 accent-accent"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <EyeOff className="w-4 h-4 text-text-secondary" />
                    <span className="font-medium text-text-primary">Private</span>
                  </div>
                  <p className="text-sm text-text-secondary mt-0.5">
                    Hidden from search. New members can only join via invite link.
                  </p>
                </div>
              </label>
            </div>
          </section>

          {/* Content Moderation Section */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
              <MessageSquareWarning className="w-5 h-5 text-accent" />
              Content Moderation
            </h2>

            <div className="bg-oro-surface border border-oro-border rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-text-primary">Clean language filter</p>
                  <p className="text-xs text-text-muted mt-1">
                    When enabled, profanity is blocked in chat and event descriptions. Slurs and hate speech are always blocked regardless of this setting.
                  </p>
                </div>
                <button
                  onClick={handleToggleCleanLanguage}
                  disabled={updateClubMutation.isPending}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-oro-bg disabled:opacity-50 ${
                    club.clean_language_enabled !== 0 ? 'bg-accent' : 'bg-oro-surface-alt'
                  }`}
                  role="switch"
                  aria-checked={club.clean_language_enabled !== 0}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      club.clean_language_enabled !== 0 ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
              <p className="text-xs text-text-tertiary mt-3">
                Note: Club names, user profiles, and discriminatory language are always filtered.
              </p>
            </div>
          </section>

          {/* Invite Permissions Section */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
              <Shield className="w-5 h-5 text-accent" />
              Invite Permissions
            </h2>

            <div className="bg-oro-surface border border-oro-border rounded-2xl p-4 space-y-3">
              <p className="text-sm text-text-secondary mb-2">
                Choose who can send invites to new members.
              </p>

              {/* All Members Option */}
              <label
                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                  (club.invite_permission || 'all_members') === 'all_members'
                    ? 'border-accent bg-accent/5'
                    : 'border-oro-border hover:border-oro-border-hover'
                }`}
              >
                <input
                  type="radio"
                  name="invite_permission"
                  value="all_members"
                  checked={(club.invite_permission || 'all_members') === 'all_members'}
                  onChange={() => handleInvitePermissionChange('all_members')}
                  disabled={updateClubMutation.isPending}
                  className="mt-1 accent-accent"
                />
                <div className="flex-1">
                  <span className="font-medium text-text-primary">All Members</span>
                  <p className="text-sm text-text-secondary mt-0.5">
                    Any club member can invite new people to join.
                  </p>
                </div>
              </label>

              {/* Leaders Only Option */}
              <label
                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                  club.invite_permission === 'leaders_only'
                    ? 'border-accent bg-accent/5'
                    : 'border-oro-border hover:border-oro-border-hover'
                }`}
              >
                <input
                  type="radio"
                  name="invite_permission"
                  value="leaders_only"
                  checked={club.invite_permission === 'leaders_only'}
                  onChange={() => handleInvitePermissionChange('leaders_only')}
                  disabled={updateClubMutation.isPending}
                  className="mt-1 accent-accent"
                />
                <div className="flex-1">
                  <span className="font-medium text-text-primary">Leaders Only</span>
                  <p className="text-sm text-text-secondary mt-0.5">
                    Only club owners and leaders can send invites.
                  </p>
                </div>
              </label>

              {/* Disabled Option */}
              <label
                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                  club.invite_permission === 'disabled'
                    ? 'border-accent bg-accent/5'
                    : 'border-oro-border hover:border-oro-border-hover'
                }`}
              >
                <input
                  type="radio"
                  name="invite_permission"
                  value="disabled"
                  checked={club.invite_permission === 'disabled'}
                  onChange={() => handleInvitePermissionChange('disabled')}
                  disabled={updateClubMutation.isPending}
                  className="mt-1 accent-accent"
                />
                <div className="flex-1">
                  <span className="font-medium text-text-primary">Disabled</span>
                  <p className="text-sm text-text-secondary mt-0.5">
                    No one can send invites. New members must request to join.
                  </p>
                </div>
              </label>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
